/**
 * This code was  originally copied from the lexical playground source code on github
 * And so it doesn't follow our code style
 * https://github.com/facebook/lexical/tree/main/packages/lexical-playground
 *
 */
import "./index.css";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import AddIcon from "@mui/icons-material/Add";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ModalBox from "components/Modal";
import InfoBlue from "components/shared/InfoBlockBlue";
import StyledButton from "components/shared/StyledButton";
import StyledCheckbox from "components/shared/StyledCheckbox";
import ThreeDotMenu from "components/shared/ThreeDotMenu";
import { BodyHighlight, SmallBodyHighlight } from "helpers/TypographyComponents";
import type { LexicalEditor, ParagraphNode } from "lexical";
import {
    $createParagraphNode,
    $getNearestNodeFromDOMNode,
    $nodesOfType,
    isHTMLElement,
    LexicalNode,
    TextNode,
} from "lexical";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { INSERT_ARTICLE_COMMAND, recalculateNumberingArticles } from "../ArticlePlugin";
import { ArticleContainerNode } from "../ArticlePlugin/ArticleContainerNode";
import { ArticleTitleNode } from "../ArticlePlugin/ArticleTitleNode";
import { hasLetterNumbering } from "../utils";
import { Point } from "./Point";
import { Rect } from "./Rect";

const SPACE = 4;
const TARGET_LINE_HALF_HEIGHT = 2;
const DRAGGABLE_BLOCK_MENU_CLASSNAME = "draggable-block-menu";
const DRAG_DATA_FORMAT = "application/x-lexical-drag-block";
const TEXT_BOX_HORIZONTAL_PADDING = 28;
const TOOLBAR_HEIGHT = 56;

const Downward = 1;
const Upward = -1;
const Indeterminate = 0;

let prevIndex = Infinity;

function getCurrentIndex(keysLength: number): number {
    if (keysLength === 0) {
        return Infinity;
    }
    if (prevIndex >= 0 && prevIndex < keysLength) {
        return prevIndex;
    }

    return Math.floor(keysLength - 1);
}

function getArticleContainerNodeKeys(editor: LexicalEditor): string[] {
    // return editor.getEditorState().read(() => $getRoot().getChildrenKeys());
    return editor.getEditorState().read(() => $nodesOfType(ArticleContainerNode).map((node) => node.getKey()));
}

function getCollapsedMargins(elem: HTMLElement): {
    marginTop: number;
    marginBottom: number;
} {
    const getMargin = (element: Element | null, margin: "marginTop" | "marginBottom"): number =>
        element ? parseFloat(window.getComputedStyle(element)[margin]) : 0;

    const { marginTop, marginBottom } = window.getComputedStyle(elem);
    const prevElemSiblingMarginBottom = getMargin(elem.previousElementSibling, "marginBottom");
    const nextElemSiblingMarginTop = getMargin(elem.nextElementSibling, "marginTop");
    const collapsedTopMargin = Math.max(parseFloat(marginTop), prevElemSiblingMarginBottom);
    const collapsedBottomMargin = Math.max(parseFloat(marginBottom), nextElemSiblingMarginTop);

    return { marginBottom: collapsedBottomMargin, marginTop: collapsedTopMargin };
}

function getBlockElement(
    anchorElem: HTMLElement,
    editor: LexicalEditor,
    event: MouseEvent,
    useEdgeAsDefault = false,
): HTMLElement | null {
    const anchorElementRect = anchorElem.getBoundingClientRect();
    const topLevelNodeKeys = getArticleContainerNodeKeys(editor);

    let blockElem: HTMLElement | null = null;

    editor.getEditorState().read(() => {
        if (useEdgeAsDefault) {
            const [firstNode, lastNode] = [
                editor.getElementByKey(topLevelNodeKeys[0]),
                editor.getElementByKey(topLevelNodeKeys[topLevelNodeKeys.length - 1]),
            ];

            const [firstNodeRect, lastNodeRect] = [
                firstNode?.getBoundingClientRect(),
                lastNode?.getBoundingClientRect(),
            ];

            if (firstNodeRect && lastNodeRect) {
                if (event.y < firstNodeRect.top) {
                    blockElem = firstNode;
                } else if (event.y > lastNodeRect.bottom) {
                    blockElem = lastNode;
                }

                if (blockElem) {
                    return;
                }
            }
        }

        let index = getCurrentIndex(topLevelNodeKeys.length);
        let direction = Indeterminate;

        while (index >= 0 && index <= topLevelNodeKeys.length) {
            const key = topLevelNodeKeys[index];
            const elem = editor.getElementByKey(key);

            if (elem === null) {
                break;
            }
            const point = new Point(event.x, event.y);
            const domRect = Rect.fromDOM(elem);
            const { marginTop, marginBottom } = getCollapsedMargins(elem);

            const rect = domRect.generateNewRect({
                bottom: domRect.bottom + marginBottom,
                left: anchorElementRect.left,
                right: anchorElementRect.right,
                top: domRect.top - marginTop,
            });

            const {
                result,
                reason: { isOnTopSide, isOnBottomSide },
            } = rect.contains(point);

            if (result) {
                blockElem = elem;
                prevIndex = topLevelNodeKeys.length;
                break;
            }

            if (direction === Indeterminate) {
                if (isOnTopSide) {
                    direction = Upward;
                } else if (isOnBottomSide) {
                    direction = Upward;
                } else {
                    // stop search block element
                    direction = Infinity;
                }
            }

            index += direction;
        }
    });

    return blockElem;
}

function isOnMenu(element: HTMLElement): boolean {
    return !!element.closest(`.${DRAGGABLE_BLOCK_MENU_CLASSNAME}`);
}

function setMenuPosition(targetElem: HTMLElement | null, floatingElem: HTMLElement, anchorElem: HTMLElement) {
    if (!targetElem) {
        floatingElem.style.opacity = "0";
        floatingElem.style.visibility = "hidden";
        // floatingElem.style.transform = "translate(-10000px, -10000px)";
        return;
    }

    const targetRect = targetElem.getBoundingClientRect();
    const targetStyle = window.getComputedStyle(targetElem);
    const floatingElemRect = floatingElem.getBoundingClientRect();
    const anchorElementRect = anchorElem.getBoundingClientRect();

    const top =
        targetRect.top +
        (parseInt(targetStyle.lineHeight, 10) - floatingElemRect.height) / 2 -
        anchorElementRect.top +
        TOOLBAR_HEIGHT;

    const left = SPACE;

    floatingElem.style.opacity = "1";
    floatingElem.style.visibility = "visible";
    floatingElem.style.transform = `translate(${left}px, ${top}px)`;
}

function useDraggableBlockMenu(editor: LexicalEditor, anchorElem: HTMLElement, isEditable: boolean): JSX.Element {
    const scrollerElem = anchorElem.parentElement;
    const contentEditableRootElem = anchorElem.firstChild;

    const [t] = useTranslation();

    const menuRef = useRef<HTMLDivElement>(null);
    const [draggableBlockElem, setDraggableBlockElem] = useState<HTMLElement | null>(null);
    const [scrollTop, setScrollTop] = useState(0);
    const [showOptionalModal, setShowOptionalModal] = useState(false);
    const [makeOptional, setMakeOptional] = useState(false);
    const [articleContainerElement, setArticleContainerElement] = useState<HTMLElement | null>(null);
    const [articleContainerNode, setArticleContainerNode] = useState<ArticleContainerNode | null>(null);
    const [isOptional, setIsOptional] = useState(false);
    const [numbering, setNumbering] = useState<"numerical" | "alphabetical">("numerical");

    useEffect(() => {
        if (!articleContainerElement) return;
        // Render draggable menu option numbering
        const articleTitle =
            Array.from(articleContainerElement?.children).find((htmlEl) => htmlEl.className.includes("Article__title"))
                ?.textContent ?? "";
        if (hasLetterNumbering(articleTitle)) {
            setNumbering("alphabetical");
        } else {
            setNumbering("numerical");
        }
        editor.update(() => {
            const node: ArticleContainerNode | null = $getNearestNodeFromDOMNode(
                articleContainerElement,
            ) as ArticleContainerNode;
            setArticleContainerNode(node);
            setIsOptional(node.getConditional());
        });
        // If article is deleted, remove the floating options block
        editor.registerMutationListener(ArticleContainerNode, (mutatedNodes) => {
            for (const [nodeKey, mutation] of mutatedNodes) {
                if (mutation == "destroyed") setDraggableBlockElem(null);
            }
        });
    }, [articleContainerElement, editor]);

    const handleOpenOptionalModal = () => {
        setShowOptionalModal(true);
        editor.update(() => {
            if (!articleContainerNode) return;
            setMakeOptional(articleContainerNode.getConditional());
        });
    };

    const handleMakeOptional = () => {
        setShowOptionalModal(false);
        editor.update(() => {
            if (!articleContainerNode) return;
            articleContainerNode.setConditional(!isOptional);
            articleContainerNode.getLastChild()?.selectEnd().toggleFormat("bold");
            articleContainerNode.select();
            setTimeout(() => {
                setIsOptional(!isOptional);
            }, 300);
            recalculateNumberingArticles(true, true);
        });
    };

    const handleInsertArticle = (placement: "before" | "after") => {
        editor.update(() => {
            if (!articleContainerNode) return;
            articleContainerNode?.select();
            let newNode: ParagraphNode | null;
            let prevSiblings = articleContainerNode?.getPreviousSiblings();
            let nextSiblings = articleContainerNode.getNextSiblings();

            // When the article is preceded or followed by a paragraph, it must be created before or after this one
            if (placement === "before") {
                // If article container node has no prev or next paragraphs with text then add/select an empty one
                if (!prevSiblings.length || prevSiblings[0].getTextContent().length) {
                    articleContainerNode.insertBefore($createParagraphNode());
                    prevSiblings = articleContainerNode?.getPreviousSiblings();
                }
                newNode = prevSiblings.reverse()[0].insertBefore($createParagraphNode()) as ParagraphNode;
            } else {
                if (!nextSiblings.length || nextSiblings[0].getTextContent().length) {
                    articleContainerNode.insertAfter($createParagraphNode());
                    nextSiblings = articleContainerNode.getNextSiblings();
                }
                newNode = nextSiblings[0].insertAfter($createParagraphNode()) as ParagraphNode;
            }
            newNode?.select();
            editor.dispatchCommand(INSERT_ARTICLE_COMMAND, { getCurrentSelection: true });
        });
    };

    useEffect(() => {
        function onMouseMove(event: MouseEvent) {
            const target = event.target;
            if (!isHTMLElement(target!)) {
                setDraggableBlockElem(null);
                return;
            }

            if (isOnMenu(target)) {
                return;
            }

            if (typeof target.className !== "string" || !target.className.includes("Article__")) {
                return;
            }

            const _draggableBlockElem = getBlockElement(anchorElem, editor, event);

            setDraggableBlockElem(_draggableBlockElem);
            setArticleContainerElement(_draggableBlockElem);
        }

        function onMouseLeave() {
            setDraggableBlockElem(null);
        }

        scrollerElem?.addEventListener("mousemove", onMouseMove);
        scrollerElem?.addEventListener("mouseleave", onMouseLeave);
        contentEditableRootElem?.addEventListener("scroll", onMouseLeave);

        return () => {
            scrollerElem?.removeEventListener("mousemove", onMouseMove);
            scrollerElem?.removeEventListener("mouseleave", onMouseLeave);
            contentEditableRootElem?.removeEventListener("scroll", onMouseLeave);
        };
    }, [scrollerElem, anchorElem, editor]);

    useEffect(() => {
        if (menuRef.current) {
            setMenuPosition(draggableBlockElem, menuRef.current, anchorElem);
        }
    }, [anchorElem, draggableBlockElem, scrollTop]);

    useEffect(() => {
        const onScroll = (e) => {
            setScrollTop(e.target.scrollTop);
        };
        anchorElem?.firstChild?.addEventListener("scroll", onScroll);

        return () => window.removeEventListener("scroll", onScroll);
    }, [scrollTop]);

    const handleInsertLineBreak = (placement: "before" | "after") => {
        editor.update(() => {
            if (!articleContainerNode) return;
            articleContainerNode?.select();

            if (placement === "before") {
                articleContainerNode.insertBefore($createParagraphNode());
            }
            if (placement === "after") {
                articleContainerNode.insertAfter($createParagraphNode());
            }
        });
    };

    /**
     * Handle change numbering, it can be alphebetical a), b), c) or numerical 1 | 1.1 | 1.2.1
     * @param numbering current numbering type
     */
    const handleNumbering = (numbering: "alphabetical" | "numerical") => {
        const format = {
            alphabetical: () => {
                editor.update(() => {
                    // make sure to set lexical selection() before recalculateNumberingArticles()
                    if (!articleContainerNode) return;
                    articleContainerNode.select();
                    recalculateNumberingArticles(true, true, "numerical", true);
                    setTimeout(() => {
                        setNumbering("numerical");
                    }, 300);
                });
            },
            numerical: () => {
                editor.update(() => {
                    if (!articleContainerNode) return;
                    articleContainerNode.select();
                    recalculateNumberingArticles(true, true, "alphabetical", true);
                    setTimeout(() => {
                        setNumbering("alphabetical");
                    }, 300);
                });
            },
        };

        return format[numbering];
    };

    return createPortal(
        <>
            <div
                className="icon draggable-block-menu"
                ref={menuRef}
                // onClick={handleOpenOptionalModal}
                // draggable={true}
                // onDragStart={onDragStart}
                // onDragEnd={onDragEnd}
            >
                {/* <div className={isEditable ? "icon" : ""} /> */}
                {/* <AddIcon sx={{ opacity: 0.5 }} /> */}
                <ThreeDotMenu
                    menuIcon={<AddIcon sx={{ opacity: 0.5, height: "24px", width: "24px" }} />}
                    options={[
                        {
                            id: 1,
                            name: t("RichEditor.Numbering"),
                            action: handleNumbering(numbering),
                            icon: (
                                <SmallBodyHighlight color="grey">
                                    {numbering === "alphabetical" ? "1 2 3" : "A B C"}
                                </SmallBodyHighlight>
                            ),
                            iconPosition: "after",
                        },
                        {
                            id: 2,
                            name: isOptional ? t("RichEditor.MakeOptionalRemove") : t("RichEditor.MakeOptionalTitle"),
                            action: isOptional ? handleMakeOptional : handleOpenOptionalModal,
                        },
                        {
                            id: 3,
                            name: t("RichEditor.InsertArticleBefore"),
                            action: () => handleInsertArticle("before"),
                        },
                        { id: 4, name: t("RichEditor.InsertArticleAfter"), action: () => handleInsertArticle("after") },
                        {
                            id: 5,
                            name: t("RichEditor.InsertLineBreakBefore"),
                            action: () => handleInsertLineBreak("before"),
                        },
                        {
                            id: 6,
                            name: t("RichEditor.InsertLineBreakAfter"),
                            action: () => handleInsertLineBreak("after"),
                        },
                    ]}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                />
            </div>
            <ModalBox
                open={showOptionalModal}
                mainTitle={t("RichEditor.MakeOptionalTitle")}
                onClose={() => setShowOptionalModal(false)}
                style={{ width: "600px" }}
            >
                <>
                    <InfoBlue info={t("RichEditor.MakeOptionalDescription")} />
                    <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "1rem" }}>
                        <StyledCheckbox
                            icon={<CheckBoxOutlineBlankIcon />}
                            checkedIcon={<CheckBoxIcon color="primary" />}
                            checked={makeOptional}
                            onChange={(e) => setMakeOptional(e.target.checked)}
                            size="small"
                            sx={{ opacity: 0.8 }}
                            id="optional"
                        />
                        <BodyHighlight color="black" tag="label" htmlFor="optional">
                            {t("RichEditor.MakeOptionalCheckbox")}
                        </BodyHighlight>
                    </div>
                    <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                        <StyledButton
                            buttonType="primary"
                            fullWidth
                            onClick={handleMakeOptional}
                            disabled={!makeOptional}
                        >
                            {t("RichEditor.MakeOptionalValidate")}
                        </StyledButton>
                    </div>
                </>
            </ModalBox>
        </>,
        anchorElem,
    );
}

export default function DraggableBlockPlugin({
    anchorElem = document.body,
}: {
    anchorElem?: HTMLElement;
}): JSX.Element {
    const [editor] = useLexicalComposerContext();
    return useDraggableBlockMenu(editor, anchorElem, editor._editable);
}
